// src/components/roles/RolePermissionsModal.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { assignPermissionsToRole } from '../../store/actions/roleActions';
import { fetchPermissions } from '../../store/actions/permissionActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';

const RolePermissionsModal = ({ isOpen, onClose, role }) => {
  const dispatch = useDispatch();
  const { permissions, loading } = useSelector((state) => state.permissions);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedModules, setExpandedModules] = useState({});

  // Styles object
  const styles = {
    overlay: {
      position: 'fixed',
      inset: 0,
      zIndex: 50,
      overflowY: 'auto',
      animation: 'fadeIn 0.2s ease-out',
    },
    overlayBackground: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      transition: 'opacity 0.2s',
    },
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '1rem',
      textAlign: 'center',
    },
    modal: {
      display: 'inline-block',
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      transform: 'translateY(0)',
      transition: 'all 0.2s',
      maxWidth: '56rem',
      width: '100%',
      animation: 'slideUp 0.3s ease-out',
    },
    content: {
      padding: '1.5rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
    },
    title: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: '1.5rem',
      color: '#111827',
      margin: 0,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      color: '#9ca3af',
      padding: '0.25rem',
      lineHeight: 1,
      transition: 'color 0.2s',
      borderRadius: '0.375rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    closeButtonHover: {
      color: '#6b7280',
      backgroundColor: '#f3f4f6',
    },
    
    // Role info
    roleInfo: {
      backgroundColor: '#f9fafb',
      borderRadius: '0.375rem',
      padding: '1rem',
      marginBottom: '1rem',
    },
    roleName: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '0.25rem',
    },
    roleDescription: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    
    // Search and stats
    searchContainer: {
      marginBottom: '1rem',
    },
    searchInput: {
      width: '100%',
      padding: '0.625rem 1rem 0.625rem 2.5rem',
      fontSize: '0.875rem',
      color: '#111827',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'all 0.2s',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: '0.75rem center',
      backgroundSize: '1.25rem',
    },
    searchInputFocus: {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    
    statsContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
      padding: '0.5rem 0',
      borderBottom: '1px solid #e5e7eb',
    },
    statsSelected: {
      fontSize: '0.875rem',
      color: '#2563eb',
      fontWeight: 500,
    },
    statsTotal: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    selectAllButton: {
      padding: '0.25rem 0.75rem',
      backgroundColor: '#f3f4f6',
      color: '#4b5563',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    selectAllButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    
    // Permissions container
    permissionsContainer: {
      maxHeight: '400px',
      overflowY: 'auto',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      padding: '0.5rem',
      marginBottom: '1rem',
    },
    moduleGroup: {
      marginBottom: '1rem',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      overflow: 'hidden',
    },
    moduleHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.75rem 1rem',
      backgroundColor: '#f9fafb',
      cursor: 'pointer',
      borderBottom: '1px solid #e5e7eb',
      transition: 'background-color 0.2s',
    },
    moduleHeaderHover: {
      backgroundColor: '#f3f4f6',
    },
    moduleTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    moduleName: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#111827',
    },
    moduleCount: {
      fontSize: '0.75rem',
      color: '#6b7280',
      backgroundColor: '#f3f4f6',
      padding: '0.125rem 0.375rem',
      borderRadius: '9999px',
    },
    moduleCheckbox: {
      width: '1rem',
      height: '1rem',
      accentColor: '#2563eb',
      cursor: 'pointer',
    },
    moduleIcon: {
      width: '1.25rem',
      height: '1.25rem',
      color: '#6b7280',
      transition: 'transform 0.2s',
    },
    moduleIconExpanded: {
      transform: 'rotate(90deg)',
    },
    
    permissionsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '0.5rem',
      padding: '1rem',
    },
    permissionItem: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: '0.5rem',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    permissionItemHover: {
      backgroundColor: '#f9fafb',
    },
    permissionItemSelected: {
      backgroundColor: '#eff6ff',
    },
    permissionCheckbox: {
      marginTop: '0.125rem',
      width: '1rem',
      height: '1rem',
      accentColor: '#2563eb',
      cursor: 'pointer',
    },
    permissionContent: {
      marginLeft: '0.5rem',
      flex: 1,
    },
    permissionName: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#111827',
    },
    permissionDescription: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    
    emptyState: {
      padding: '2rem',
      textAlign: 'center',
      color: '#6b7280',
    },
    loadingContainer: {
      padding: '3rem',
      textAlign: 'center',
    },
    
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.75rem',
      marginTop: '1rem',
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    saveButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    saveButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    saveButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };

  // Load permissions when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchPermissions({ limit: 500 }));
    }
  }, [dispatch, isOpen]);

  // Initialize selected permissions from role's current permissions
  useEffect(() => {
    if (role?.rolePermissions && isOpen) {
      const permissionIds = role.rolePermissions.map(rp => rp.permission.id);
      setSelectedPermissions(permissionIds);
    } else {
      setSelectedPermissions([]);
    }
  }, [role, isOpen]);

  // Group permissions by module
  const groupedPermissions = useMemo(() => {
    const grouped = {};
    permissions.forEach(perm => {
      if (!grouped[perm.module]) {
        grouped[perm.module] = [];
      }
      grouped[perm.module].push(perm);
    });
    return Object.keys(grouped)
      .sort()
      .reduce((acc, module) => {
        acc[module] = grouped[module].sort((a, b) => a.name.localeCompare(b.name));
        return acc;
      }, {});
  }, [permissions]);

  // Filter permissions based on search term
  const filteredGroupedPermissions = useMemo(() => {
    if (!searchTerm) return groupedPermissions;

    const filtered = {};
    Object.entries(groupedPermissions).forEach(([module, perms]) => {
      const matchingPerms = perms.filter(perm =>
        perm.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (perm.description && perm.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      if (matchingPerms.length > 0) {
        filtered[module] = matchingPerms;
      }
    });
    return filtered;
  }, [groupedPermissions, searchTerm]);

  // Calculate selected counts per module
  const moduleSelectedCounts = useMemo(() => {
    const counts = {};
    Object.entries(groupedPermissions).forEach(([module, perms]) => {
      counts[module] = perms.filter(perm => selectedPermissions.includes(perm.id)).length;
    });
    return counts;
  }, [groupedPermissions, selectedPermissions]);

  const handlePermissionToggle = useCallback((permissionId) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  }, []);

  const handleModuleToggle = useCallback((module, modulePermissions) => {
    const modulePermIds = modulePermissions.map(p => p.id);
    const allSelected = modulePermIds.every(id => selectedPermissions.includes(id));

    if (allSelected) {
      setSelectedPermissions(prev => prev.filter(id => !modulePermIds.includes(id)));
    } else {
      setSelectedPermissions(prev => [...new Set([...prev, ...modulePermIds])]);
    }
  }, [selectedPermissions]);

  const handleSelectAll = useCallback(() => {
    const allPermissionIds = permissions.map(p => p.id);
    if (selectedPermissions.length === allPermissionIds.length) {
      setSelectedPermissions([]);
    } else {
      setSelectedPermissions(allPermissionIds);
    }
  }, [permissions, selectedPermissions]);

  const toggleModule = useCallback((module) => {
    setExpandedModules(prev => ({
      ...prev,
      [module]: !prev[module],
    }));
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await dispatch(assignPermissionsToRole(role.id, selectedPermissions));
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to assign permissions');
    } finally {
      setSaving(false);
    }
  }, [dispatch, role, selectedPermissions, onClose]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.overlayBackground} />
      
      <div style={styles.container}>
        <div style={styles.modal}>
          <div style={styles.content}>
            {/* Header */}
            <div style={styles.header}>
              <h3 style={styles.title}>
                Manage Permissions for {role?.name}
              </h3>
              <button
                onClick={onClose}
                style={styles.closeButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = styles.closeButtonHover.color;
                  e.currentTarget.style.backgroundColor = styles.closeButtonHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = styles.closeButton.color;
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                aria-label="Close dialog"
              >
                ×
              </button>
            </div>

            {/* Role Info */}
            {role && (
              <div style={styles.roleInfo}>
                <div style={styles.roleName}>{role.name}</div>
                {role.description && (
                  <div style={styles.roleDescription}>{role.description}</div>
                )}
              </div>
            )}

            {error && <Alert type="error" message={error} onClose={() => setError(null)} />}

            {/* Search */}
            <div style={styles.searchContainer}>
              <input
                type="text"
                placeholder="Search permissions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = styles.searchInputFocus.borderColor;
                  e.currentTarget.style.boxShadow = styles.searchInputFocus.boxShadow;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Stats and Select All */}
            <div style={styles.statsContainer}>
              <span style={styles.statsSelected}>
                {selectedPermissions.length} selected
              </span>
              <span style={styles.statsTotal}>
                of {permissions.length} permissions
              </span>
              <button
                onClick={handleSelectAll}
                style={styles.selectAllButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.selectAllButtonHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = styles.selectAllButton.backgroundColor;
                }}
              >
                {selectedPermissions.length === permissions.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            {/* Permissions List */}
            {loading ? (
              <div style={styles.loadingContainer}>
                <LoadingSpinner />
              </div>
            ) : (
              <div style={styles.permissionsContainer}>
                {Object.keys(filteredGroupedPermissions).length > 0 ? (
                  Object.entries(filteredGroupedPermissions).map(([module, perms]) => {
                    const isExpanded = expandedModules[module] !== false;
                    const moduleSelectedCount = moduleSelectedCounts[module] || 0;
                    const allModuleSelected = moduleSelectedCount === perms.length;
                    
                    return (
                      <div key={module} style={styles.moduleGroup}>
                        {/* Module Header */}
                        <div
                          style={styles.moduleHeader}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = styles.moduleHeaderHover.backgroundColor;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = styles.moduleHeader.backgroundColor;
                          }}
                          onClick={() => toggleModule(module)}
                        >
                          <div style={styles.moduleTitle}>
                            <input
                              type="checkbox"
                              checked={allModuleSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleModuleToggle(module, perms);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={styles.moduleCheckbox}
                            />
                            <span style={styles.moduleName}>{module}</span>
                            <span style={styles.moduleCount}>
                              {perms.length} {perms.length === 1 ? 'permission' : 'permissions'}
                            </span>
                          </div>
                          <svg
                            style={{
                              ...styles.moduleIcon,
                              ...(isExpanded ? styles.moduleIconExpanded : {}),
                            }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>

                        {/* Permissions Grid */}
                        {isExpanded && (
                          <div style={styles.permissionsGrid}>
                            {perms.map((permission) => {
                              const isSelected = selectedPermissions.includes(permission.id);
                              
                              return (
                                <div
                                  key={permission.id}
                                  style={{
                                    ...styles.permissionItem,
                                    ...(isSelected ? styles.permissionItemSelected : {}),
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.backgroundColor = styles.permissionItemHover.backgroundColor;
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                    }
                                  }}
                                  onClick={() => handlePermissionToggle(permission.id)}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {}}
                                    style={styles.permissionCheckbox}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <div style={styles.permissionContent}>
                                    <div style={styles.permissionName}>{permission.name}</div>
                                    {permission.description && (
                                      <div style={styles.permissionDescription}>
                                        {permission.description}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div style={styles.emptyState}>
                    No permissions found matching "{searchTerm}"
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div style={styles.actions}>
              <button
                type="button"
                onClick={onClose}
                style={styles.cancelButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || loading}
                style={{
                  ...styles.saveButton,
                  ...((saving || loading) ? styles.saveButtonDisabled : {}),
                }}
                onMouseEnter={(e) => {
                  if (!saving && !loading) {
                    e.currentTarget.style.backgroundColor = styles.saveButtonHover.backgroundColor;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!saving && !loading) {
                    e.currentTarget.style.backgroundColor = styles.saveButton.backgroundColor;
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default RolePermissionsModal;